import React, { useEffect, useMemo, useState } from 'react';
import { keywordsCache } from '../utils/keywordsCache';
import { apiService, ProfileFieldSpec, ProfileSpec } from '../services/apiService';

/**
 * The profile form, rendered from the server's field spec.
 *
 * Previously the field list lived in JSX and the weights behind the
 * completion bar lived in a second copy in the browser — the score the user
 * saw was computed from a table that summed to 120 and was clamped at 100, so
 * "100% complete" appeared with fields still empty. Everything here comes
 * from GET /api/v1/profile/spec: which fields exist, what they are worth,
 * what the dropdowns contain, and what is compulsory.
 *
 * Retired fields (hats, intent, qualification, the old R&D block) are simply
 * absent from the spec, so they stop rendering without any code here knowing
 * their names.
 */

export type ProfileValues = Record<string, any>;

const isFilled = (f: ProfileFieldSpec, v: any): boolean => {
  if (v === null || v === undefined) return false;
  if (f.kind === 'multiselect' || f.kind === 'keywords' || f.kind === 'nested_multiselect'
      || f.kind === 'single_chip' || f.kind === 'dependent_checkboxes') {
    return Array.isArray(v) && v.length > 0;
  }
  if (f.kind === 'longtext') {
    return typeof v === 'string' && v.trim().length >= (f.min_length ?? 1);
  }
  if (f.kind === 'longtext_list') {
    return Array.isArray(v) && v.some((x: any) =>
      typeof x === 'string' && x.trim().length >= (f.min_length ?? 1));
  }
  return String(v).trim().length > 0;
};

/** Completion from the same weights the server uses. Totals 100 — no clamp. */
export const scoreProfile = (spec: ProfileSpec | null, values: ProfileValues) => {
  if (!spec) return { percentage: 0, groups: {} as Record<string, number>, missing: [] as string[] };
  let earned = 0;
  const per: Record<string, { earned: number; possible: number }> = {};
  const missing: string[] = [];
  spec.fields.forEach(f => {
    per[f.group] = per[f.group] || { earned: 0, possible: 0 };
    per[f.group].possible += f.weight;
    if (isFilled(f, values[f.key])) {
      earned += f.weight;
      per[f.group].earned += f.weight;
    } else if (f.weight > 0) missing.push(f.key);
  });
  const groups: Record<string, number> = {};
  Object.entries(per).forEach(([k, v]) =>
    groups[k] = v.possible ? Math.round((v.earned / v.possible) * 100) : 0);
  return { percentage: earned, groups, missing };
};

const label = 'text-grayscale-1100 font-grotesk text-xs';
const box = 'h-11 py-3 px-2.5 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full';

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean }> =
  ({ on, onClick, children, disabled }) => (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`px-3 py-1.5 rounded-full border text-xs transition-colors disabled:opacity-40 ${
        on ? 'bg-secondary-300/20 border-secondary-300 text-[#2C5695] font-medium'
           : 'bg-white border-grayscale-300 text-grayscale-1200 hover:border-primary-300'}`}>
      {children}
    </button>
  );

interface FieldProps {
  field: ProfileFieldSpec;
  spec: ProfileSpec;
  value: any;
  values: ProfileValues;
  onChange: (key: string, value: any) => void;
}

type Goal = { label: string; description?: string; chip?: string };

/** Persona-qualified goal value. Industry and Consultant were offered the same
 *  five goals when qualification was introduced (Consultant has its own list
 *  now), and a bare label could not say which of them it was answered for —
 *  which is how ticking a goal under Industry also ticked it under Consultant,
 *  and how the reverse-lookup that reads goals back to personas came to select
 *  Consultant on its own. */
const QUALIFIER = '::';
export const goalKey = (persona: string, label: string) => `${persona}${QUALIFIER}${label}`;
/** Split a stored value. Entries written before qualification exist and carry
 *  no persona; they belong to whoever owns the label, which is the old
 *  behaviour and the only reading that does not silently drop them. */
export const splitGoal = (v: string): { persona: string | null; label: string } => {
  const i = String(v).indexOf(QUALIFIER);
  return i < 0 ? { persona: null, label: String(v) }
               : { persona: String(v).slice(0, i), label: String(v).slice(i + QUALIFIER.length) };
};

/**
 * Goals, one block per persona the user picked.
 *
 * These used to be tabs, and personas offering an identical list were merged
 * into a single tab titled "Industry / Consultant". Both decisions caused the
 * bugs this replaces: the merge made two personas share one set of answers,
 * and the tab row — a line of persona-named pills that appeared the moment a
 * second persona was picked — read as the persona selector repeating itself
 * below the question.
 *
 * Each persona now gets its own headed block with its own answers. The same
 * question under two personas is two questions.
 */
const GoalPicker: React.FC<{
  f: ProfileFieldSpec;
  groups: Record<string, Goal[]>;
  parents: string[];
  picked: string[];
  onChange: (key: string, value: any) => void;
  head: React.ReactNode;
}> = ({ f, groups, parents, picked, onChange, head }) => {
  const sections = useMemo(
    () => parents
      .map(p => ({ title: p, opts: groups?.[p] || [] }))
      .filter(s => s.opts.length),
    [groups, parents]);

  if (!sections.length) return null;

  /** Ticked under THIS persona — or, for a legacy unqualified entry, under
   *  whichever persona owns the label. */
  const isOn = (persona: string, label: string) => picked.some(v => {
    const g = splitGoal(v);
    if (g.label !== label) return false;
    return g.persona === null ? (groups?.[persona] || []).some(o => o.label === label)
                              : g.persona === persona;
  });

  const toggle = (persona: string, label: string) => {
    const key = goalKey(persona, label);
    const next = isOn(persona, label)
      // Drops the qualified value AND any legacy bare one, so un-ticking
      // actually un-ticks rather than leaving the old entry behind.
      ? picked.filter(v => v !== key && v !== label)
      : [...picked.filter(v => v !== label), key];
    onChange(f.key, next);
  };

  /** Every stored value, whichever persona it was ticked under. A legacy bare
   *  label is credited to the first persona offering it. A label no persona
   *  offers any more (goals get reworded) keeps its raw text rather than
   *  vanishing — it is still stored, so it must still be removable. */
  const all = picked.map(v => {
    const g = splitGoal(v);
    const persona = g.persona
      ?? Object.keys(groups || {}).find(p => (groups?.[p] || []).some(o => o.label === g.label))
      ?? null;
    const goal = persona ? (groups?.[persona] || []).find(o => o.label === g.label) : undefined;
    return { value: v, persona, label: g.label, text: goal?.chip || g.label };
  });
  // Tag the chips whenever any belongs to a persona not on screen — with a
  // single-select persona that is the common case, and an untagged chip sat
  // above a list where nothing is ticked reads as a contradiction.
  const shown = new Set(parents);
  const tagged = all.some(c => c.persona && !shown.has(c.persona))
    || new Set(all.map(c => c.persona).filter(Boolean)).size > 1;
  // Same filter as toggle(): a qualified value takes its legacy bare twin with it.
  const remove = (c: { value: string; label: string }) =>
    onChange(f.key, picked.filter(v => v !== c.value && v !== c.label));

  return (
    <div className="flex flex-col gap-2">
      {head}
      {f.help && <span className="text-[11px] text-grayscale-1100 -mt-1">{f.help}</span>}

      {/* The persona chip is single-select, so switching it hides every other
          persona's ticks below. This row is the one place the whole selection
          is visible — and removable — at once. */}
      {all.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {all.map(c => (
            <button key={c.value} type="button" onClick={() => remove(c)}
              title={c.persona ? `${c.persona} · ${c.label}` : c.label}
              className="inline-flex items-center gap-1.5 py-1 pl-3 pr-2 rounded-full
                bg-primary-500 text-grayscale-100 text-xs font-medium">
              {tagged && c.persona && (
                <span className="font-normal opacity-70">{c.persona} ·</span>
              )}
              {c.text}
              <span aria-hidden className="text-sm leading-none opacity-80">×</span>
            </button>
          ))}
        </div>
      )}

      {sections.map((s, si) => {
        const chosen = s.opts.filter(o => isOn(s.title, o.label));
        return (
          <div key={s.title} className={si ? 'pt-3 mt-1 border-t border-grayscale-200' : ''}>
            {/* Named only when there is more than one, so a single persona
                reads as the question it is rather than a labelled subsection. */}
            {sections.length > 1 && (
              <div className="text-xs font-medium text-grayscale-1200 mb-1.5">
                As {s.title}{chosen.length ? ` · ${chosen.length} selected` : ''}
              </div>
            )}
            <div className="flex flex-col">
              {s.opts.map(o => (
                <label key={o.label}
                  className="flex items-start gap-3 py-2.5 border-b border-grayscale-200
                    last:border-b-0 cursor-pointer">
                  <input type="checkbox" checked={isOn(s.title, o.label)}
                    className="mt-0.5 w-4 h-4 shrink-0"
                    onChange={() => toggle(s.title, o.label)} />
                  <span className="text-sm">
                    <span className="text-grayscale-1300 font-medium">{o.label}</span>
                    {o.description && (
                      <span className="text-grayscale-1100"> — {o.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};


const Field: React.FC<FieldProps> = ({ field: f, spec, value, values, onChange }) => {
  const optionList = (): string[] => {
    const o = f.options ? spec.options[f.options] : undefined;
    return Array.isArray(o) ? o : [];
  };

  const required = f.required_paid || f.required_free;
  // Weights are deliberately NOT shown. They exist for internal scoring and
  // segmentation; putting "5%" next to a question turns filling in a profile
  // into a points game and tells users what we do with the answers.
  const head = (
    <div className="flex items-baseline justify-between gap-2">
      <span className={label}>
        {f.label}{required && <span className="text-red-500"> *</span>}
      </span>
    </div>
  );

  switch (f.kind) {
    case 'select': {
      return (
        <div className="flex flex-col gap-1">
          {head}
          <select value={value || ''} onChange={e => onChange(f.key, e.target.value)}
                  className={box}>
            <option value="">Select…</option>
            {/* A stored value that predates this list — Country was free text
                until recently — is kept as an option, so opening the form does
                not silently blank an answer someone already gave. */}
            {value && !optionList().includes(value) && (
              <option value={value}>{value}</option>
            )}
            {optionList().map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {f.help && <span className="text-[11px] text-grayscale-1100">{f.help}</span>}
        </div>
      );
    }
    case 'multiselect': {
      const picked: string[] = Array.isArray(value) ? value : [];
      const atLimit = !!f.max_select && picked.length >= f.max_select;
      return (
        <div className="flex flex-col gap-1.5">
          {head}
          <div className="flex flex-wrap gap-2">
            {optionList().map(o => {
              const on = picked.includes(o);
              return (
                <Chip key={o} on={on} disabled={!on && atLimit}
                  onClick={() => onChange(f.key, on ? picked.filter(x => x !== o) : [...picked, o])}>
                  {o}
                </Chip>
              );
            })}
          </div>
          {(f.help || f.max_select) && (
            <span className="text-[11px] text-grayscale-1100">
              {f.help}{f.max_select ? ` · ${picked.length}/${f.max_select} selected` : ''}
            </span>
          )}
        </div>
      );
    }
    // Single select, rendered as chips rather than a dropdown: three options
    // read better side by side, and it is the switch that decides which goals
    // appear below. Stored as a one-element array so the JSONB column and
    // every existing reader of `personas` are unchanged.
    case 'single_chip': {
      const picked: string[] = Array.isArray(value) ? value : (value ? [value] : []);
      const current = picked[0] || '';
      return (
        <div className="flex flex-col gap-1.5">
          {head}
          <div className="flex flex-wrap gap-2">
            {optionList().map(o => (
              <Chip key={o} on={current === o}
                // Re-picking the current option clears it; there is no other
                // way to undo a choice in a single-select chip group.
                onClick={() => onChange(f.key, current === o ? [] : [o])}>
                {o}
              </Chip>
            ))}
          </div>
          {f.help && <span className="text-[11px] text-grayscale-1100">{f.help}</span>}
        </div>
      );
    }
    // Checkboxes whose list depends on another field's value — the goals shown
    // are the ones belonging to the chosen persona. Renders nothing until that
    // choice is made, which is why the section looks short to a new user.
    case 'dependent_checkboxes': {
      const groups = (f.options ? spec.options[f.options] : {}) as
        Record<string, { label: string; description?: string; chip?: string }[]>;
      const parentVal = values[f.depends_on || ''];
      const parents: string[] = Array.isArray(parentVal) ? parentVal : (parentVal ? [parentVal] : []);
      const picked: string[] = Array.isArray(value) ? value : [];
      if (!parents.length) return null;
      return (
        <GoalPicker f={f} groups={groups} parents={parents} picked={picked}
          onChange={onChange} head={head} />
      );
    }
    case 'nested_multiselect': {
      // Only the details belonging to the goals the user actually picked.
      const nested = (f.options ? spec.options[f.options] : {}) as Record<string, string[]>;
      const parents: string[] = Array.isArray(values[f.depends_on || '']) ? values[f.depends_on || ''] : [];
      const picked: string[] = Array.isArray(value) ? value : [];
      if (!parents.length) return null;
      return (
        <div className="flex flex-col gap-1.5">
          {head}
          {parents.map(p => (
            <div key={p} className="flex flex-col gap-1">
              <span className="text-[11px] text-grayscale-1200 font-medium">{p}</span>
              <div className="flex flex-wrap gap-2">
                {(nested?.[p] || []).map(d => {
                  const on = picked.includes(d);
                  return (
                    <Chip key={d} on={on}
                      onClick={() => onChange(f.key, on ? picked.filter(x => x !== d) : [...picked, d])}>
                      {d}
                    </Chip>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }
    case 'keywords': {
      const picked: string[] = Array.isArray(value) ? value : [];
      const [draft, setDraft] = useState('');
      // The help text has always promised "pick from suggestions" but nothing
      // ever offered any. Same corpus keyword source Direct Search and the
      // For You tags use, so the vocabulary matches what search can find.
      const [hints, setHints] = useState<string[]>([]);
      useEffect(() => {
        const q = draft.trim();
        if (q.length < 2) { setHints([]); return; }
        let dead = false;
        const t = setTimeout(async () => {
          try {
            const found = await keywordsCache.searchKeywords(q, 8);
            if (!dead) {
              setHints((found || []).filter((k: string) =>
                !picked.some(pk => pk.toLowerCase() === String(k).toLowerCase())));
            }
          } catch { if (!dead) setHints([]); }
        }, 180);
        return () => { dead = true; clearTimeout(t); };
      }, [draft, picked.length]);
      const atLimit = !!f.max_select && picked.length >= f.max_select;
      const add = () => {
        const parts = draft.split(',').map(x => x.trim()).filter(Boolean);
        if (!parts.length) return;
        const next = [...picked];
        parts.forEach(x => {
          if (next.length < (f.max_select ?? 99)
              && !next.some(y => y.toLowerCase() === x.toLowerCase())) next.push(x);
        });
        onChange(f.key, next);
        setDraft('');
      };
      return (
        <div className="flex flex-col gap-1.5">
          {head}
          <div className="flex gap-2">
            <input value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
              // Delayed: a bare onBlur fires before a suggestion button's
              // click lands, so picking a suggestion added the half-typed
              // draft instead of the suggestion.
              onBlur={() => setTimeout(add, 150)} disabled={atLimit}
              placeholder={atLimit ? 'Limit reached' : 'Type a keyword and press Enter'}
              className={box} />
          </div>
          {!atLimit && hints.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {hints.map(h => (
                <button key={h} type="button"
                  onClick={() => {
                    if (picked.some(x => x.toLowerCase() === h.toLowerCase())) return;
                    onChange(f.key, [...picked, h]);
                    setDraft(''); setHints([]);
                  }}
                  className="py-1 px-3 rounded-full border border-primary-300 text-primary-700 text-xs hover:bg-primary-100/50">
                  + {h}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {picked.map(k => (
              <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs">
                {k}
                <button type="button" onClick={() => onChange(f.key, picked.filter(x => x !== k))}
                        className="hover:text-red-600" aria-label={`Remove ${k}`}>×</button>
              </span>
            ))}
          </div>
          <span className="text-[11px] text-grayscale-1100">
            {f.help}{f.max_select ? ` · ${picked.length}/${f.max_select}` : ''}
          </span>
        </div>
      );
    }
    case 'longtext': {
      const len = typeof value === 'string' ? value.trim().length : 0;
      const min = f.min_length ?? 0;
      return (
        <div className="flex flex-col gap-1">
          {head}
          <textarea value={value || ''} rows={5}
            onChange={e => onChange(f.key, e.target.value)}
            className="py-3 px-2.5 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full" />
          <span className={`text-[11px] ${len && len < min ? 'text-amber-700' : 'text-grayscale-1100'}`}>
            {len < min ? `${len} / ${min} characters — ${min - len} more to count` : `${len} characters`}
            {f.help ? ` · ${f.help}` : ''}
          </span>
        </div>
      );
    }
    case 'longtext_list': {
      const items: string[] = Array.isArray(value) ? value : [];
      const max = f.max_items ?? 2;
      const min = f.min_length ?? 0;
      return (
        <div className="flex flex-col gap-2">
          {head}
          {items.map((it, i) => (
            <div key={i} className="flex flex-col gap-1">
              <textarea value={it} rows={4}
                onChange={e => onChange(f.key, items.map((x, j) => j === i ? e.target.value : x))}
                className="py-3 px-2.5 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full" />
              <div className="flex justify-between text-[11px] text-grayscale-1100">
                <span>{it.trim().length} / {min} characters</span>
                <button type="button" className="underline hover:text-red-600"
                  onClick={() => onChange(f.key, items.filter((_, j) => j !== i))}>Remove</button>
              </div>
            </div>
          ))}
          {items.length < max && (
            <button type="button" onClick={() => onChange(f.key, [...items, ''])}
              className="self-start text-xs text-primary-700 underline">
              + Add another problem statement ({items.length}/{max})
            </button>
          )}
        </div>
      );
    }
    default: {
      return (
        <div className="flex flex-col gap-1">
          {head}
          <input
            type={f.kind === 'email' ? 'email' : f.kind === 'url' ? 'url' : 'text'}
            value={value || ''} readOnly={f.readonly} disabled={f.readonly}
            onChange={e => onChange(f.key, e.target.value)}
            placeholder={f.kind === 'url' ? 'https://…' : ''}
            className={`${box} ${f.readonly ? 'bg-grayscale-200 text-grayscale-1100' : ''}`} />
          {f.help && <span className="text-[11px] text-grayscale-1100">{f.help}</span>}
        </div>
      );
    }
  }
};

interface ProfileFieldsProps {
  values: ProfileValues;
  onChange: (key: string, value: any) => void;
  /** Rendered once the spec loads, so callers can show the live score. */
  onSpecLoaded?: (spec: ProfileSpec) => void;
}

const ProfileFields: React.FC<ProfileFieldsProps> = ({ values, onChange, onSpecLoaded }) => {
  const [spec, setSpec] = useState<ProfileSpec | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiService.getProfileSpec().then(res => {
      if (cancelled) return;
      if (res.success && res.data?.success) {
        setSpec(res.data);
        onSpecLoaded?.(res.data);
      } else setError(res.error || 'Could not load the profile form');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byGroup = useMemo(() => {
    if (!spec) return [];
    return spec.groups.map(g => ({
      ...g,
      fields: spec.fields.filter(f => f.group === g.key),
    }));
  }, [spec]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!spec) return <div className="text-sm text-grayscale-1100">Loading your profile…</div>;

  return (
    <div className="flex flex-col gap-6">
      {byGroup.map(g => (
        <div key={g.key} className="flex flex-col gap-4">
          <h3 className="text-grayscale-1300 font-grotesk text-sm font-bold border-b border-grayscale-300 pb-1">
            {g.label}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map(f => {
              // Wide inputs get the full row; chips and prose need the space.
              const wide = ['multiselect', 'nested_multiselect', 'longtext',
                            'longtext_list', 'keywords', 'single_chip',
                            'dependent_checkboxes'].includes(f.kind);
              const node = (
                <Field key={f.key} field={f} spec={spec} value={values[f.key]}
                       values={values} onChange={onChange} />
              );
              return wide
                ? <div key={f.key} className="md:col-span-2">{node}</div>
                : node;
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProfileFields;
